import { ValueComponent } from "components/components/Component";
import {
    BooleanDependencyCondition,
    Condition,
    DependencyCondition,
    isBooleanDependencyCondition,
    isComparisonDependencyCondition,
    isDependencyCondition,
    RemoteStatusType,
} from "./Dependency";

import get from "lodash/get";

export class DependencyEvaluator {
    /**
     * Returns a boolean or the value of the first matched remote value.
     */
    public static or(
        conditions: Condition[],
        parentModel: { [x: string]: any; }): MatchResult {
        let matchesAny = false;
        let remoteValue: any;
        for (let condition of conditions) {
            if (isDependencyCondition(condition)) {
                remoteValue = get(parentModel, condition.property);
                let matched = DependencyEvaluator.matches(remoteValue, condition);
                if (matched) {
                    matchesAny = true;
                    break;
                }
            } else if (isBooleanDependencyCondition(condition)) {
                let matchResult;
                if (condition.booleanOperator === "or") {
                    matchResult = DependencyEvaluator.or(condition.conditions, parentModel);
                } else {
                    matchResult = DependencyEvaluator.and(condition.conditions, parentModel);
                }
                if (matchResult.matches) {
                    remoteValue = matchResult.component;
                    matchesAny = true;
                    break;
                }
            } else if (isComparisonDependencyCondition(condition)) {
                let matches = this.matchesComparison(condition, parentModel);
                matchesAny = matchesAny || matches.matches;
                if (matches.matches) {
                    remoteValue = matches.component;
                }
            }
        }
        if (matchesAny) {
            return {
                matches: true,
                component: remoteValue,
            };
        } else {
            return {
                matches: false,
            };
        }
    }
    private static and(
        conditions: Condition[],
        parentModel: { [x: string]: any; }): MatchResult {
        let matchesAll = true;
        let remoteValue: any;
        for (let condition of conditions) {
            if (isDependencyCondition(condition)) {
                remoteValue = get(parentModel, condition.property);
                let matched = DependencyEvaluator.matches(remoteValue, condition);
                if (!matched) {
                    matchesAll = false;
                    break;
                }
            } else if (isBooleanDependencyCondition(condition)) {
                let matchResult;
                if (condition.booleanOperator === "or") {
                    matchResult = DependencyEvaluator.or(condition.conditions, parentModel);
                } else {
                    matchResult = DependencyEvaluator.and(condition.conditions, parentModel);
                }
                if (!matchResult.matches) {
                    remoteValue = matchResult.component;
                    matchesAll = false;
                    break;
                }

            } else if (isComparisonDependencyCondition(condition)) {
                let matches = this.matchesComparison(condition, parentModel);
                matchesAll = matchesAll && matches.matches;
                if (matches.matches) {
                    remoteValue = matches.component;
                }
            }
        }
        if (matchesAll) {
            return {
                matches: true,
                component: remoteValue,
            };
        } else {
            return {
                matches: false,
            };
        }
    }

    private static matchesComparison(condition, parentModel) {
        let matches;
        let values = [];
        for (const c of condition.conditions) {
            if (c.status === "value") {
                values.push(get(parentModel, c.property));
            }
        }
        if (condition.comparisonOperator === "equal") {
            matches = values.every((v) => v === values[0]);
        }
        if (condition.comparisonOperator === "different") {
            matches = !values.every((v) => v === values[0]);
        }

        if (matches) {
            return {
                matches: true,
                component: get(parentModel, condition.conditions[0].property),
            };
        } else {
            return {
                matches: false,
            };
        }
    }

    private static matches(remoteValue: any, condition: DependencyCondition): (boolean | string | number) {
        if (condition.status === RemoteStatusType.notvalue) {
            for (let value of condition.values) {
                if (value === remoteValue) {
                    return false;
                }
            }
            return true;
        } else if (condition.status === RemoteStatusType.value) {
            for (let value of condition.values) {
                if (value === remoteValue || (value === null && remoteValue == null)) {
                    return true;
                }
            }
        } else if (condition.status === RemoteStatusType.filled) {
            return remoteValue != null;
        } else if (condition.status === RemoteStatusType.empty) {
            return remoteValue == null;
        } else if (condition.status === RemoteStatusType.checked) {
            return remoteValue === true;
        } else if (condition.status === RemoteStatusType.unchecked) {
            return remoteValue === false;
        } else if (condition.status === RemoteStatusType.any) {
            return true;
        } else {
            return false;
        }
        return false;
    }
}

export interface MatchResult {
    matches: boolean;
    component?: ValueComponent;
}
